import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import classnames from 'classnames';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Alert as OldAlert, Col, Glyphicon, Row } from 'react-bootstrap';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Label,
  MultiSelect,
  ScrollArea,
  Switch,
} from '@cargomatic/ui';
import _ from 'lodash';
import { sluggify } from 'utils/localization/utils';
import { titleize } from 'underscore.string';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import OrdersActionCreators from 'actions/OrdersActionCreators';
import CustomerSearch from 'components/site/customer-search-alt';
import Checkbox from 'components/v2/forms/Checkbox';
import ErrorAlert from 'components/ui/ErrorAlert';
import EquipmentStore from 'stores/EquipmentStore';
import OrderCreateStore from 'stores/OrderCreateStore';
import OrdersStore from 'stores/OrdersStore';
import SessionStore from 'stores/SessionStore';
import OrderTemplatesStore from 'stores/OrderTemplatesStore';
import OrderWebAPIUtils, { getOrderTemplate } from 'utils/OrderWebAPIUtils';
import { uuid } from 'uuidv4';
import CargomaticInput from 'components/v2/input';
import { DOMESTIC, EXPORT, IMPORT, REPOSITION } from 'config/order-types';
import modes, { DRAYAGE, INTERMODAL } from 'constants/orders';
import {
  DEFAULT_WEIGHT_UNIT,
  weightUnitsOptions,
  DEFAULT_TEMPERATURE_UNIT,
  temperatureUnitsOptions,
} from 'utils/localization/types';
import { withLocaleContext } from 'providers/locale-settings-context';
import CargomaticSelect from 'components/v2/select';
import CustomerSearchWithPreferences from 'components/site/customer-search-with-preferences';
import { getCarrierPreferencesByType } from 'utils/CarrierPreferencesUtils';
import { getReferenceNumberRequirements } from 'utils/UserServiceAPIUtils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import {
  getOrderMasterReferenceType,
  mapOrderMasterReferenceBookingTimeRequirement,
  MASTER_REFERENCE_TYPES,
} from 'utils/OrderBookingUtils';
import Alert from 'components/ui/Alert';
import Tooltip from 'components/ui/Tooltip';
import { checkEquipmentExistsInBulk } from 'utils/EquipmentWebAPIUtils';
import LoadingCircle from 'components/ui/LoadingCircle';
import PlusIcon from 'components/v2/icons/PlusIcon';
import ReferenceNumberField from './ReferenceNumberField';
import LocationDetails from './create/LocationDetails';
import Documents from './create/Documents';
import LocationField from './create/LocationField';

import OrderEquipments from './equipments/OrderEquipments';
import MultiEquipmentInput from './equipments/MultiEquipmentInput';
import { uploadDocuments } from '../../../utils/DocumentsAPIUtils';
import { showToast } from '../../../stores/ToastStore';
import { orderDocTypesMap } from '../../../config/documents';
import { Info } from 'lucide-react';
import withFlags from 'components/hoc/withFlags';
import { convertCommaSeparatedStringToArray } from 'helpers/strings';

const DELIVERY = 'delivery';
const PICKUP = 'pickup';
const OverseasPort = 'Overseas Port';
const FinalDestination = 'Final Destination';
const UltimateDestination = 'Ultimate Destination';
export const TERMINAL = 'terminal';
export const CUSTOMER = 'customer';

const LIVE = 'live';
const DROP = 'drop';

const orderTypes = [
  {
    name: 'Import',
    value: IMPORT,
  },
  {
    name: 'Export',
    value: EXPORT,
  },
  {
    name: 'Domestic',
    value: DOMESTIC,
  },
  {
    name: 'Reposition',
    value: REPOSITION,
  },
];

const serviceTypes = [
  {
    name: 'LIVE_LOAD_UNLOAD',
    value: LIVE,
  },
  {
    name: 'DROP_HOOK',
    value: DROP,
  },
];

const movementTypes = [
  {
    name: 'Pickup',
    value: PICKUP,
  },
  {
    name: 'Delivery',
    value: DELIVERY,
  },
];

const EXCLUDED_REFERENCE_NUMBERS = [
  'order_id',
  'payout_number',
  'shipment_id',
  'invoice_id',
];

const hazmatClasses = ['N/A', 1, 2, 3, 4, 5, 6, 7, 8, 9];

const hazmatPackagingGroup = ['N/A', 'I', 'II', 'III'];
const hazmatInitState = {
  hazmat_class: 'N/A',
  hazmat_packaging_group: 'N/A',
  hazmat_description: '',
};
class CreateOrderModal extends Component {
  static propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    flags: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = getInitialState(props.localeData, props.initialState);
    this.errorRef = React.createRef();
    this.debouncedFunctions = {};
  }

  async retrieveOceanScacs() {
    const { t } = this.props;
    let ocean_scacs = await OrderWebAPIUtils.getCarrierScacs();
    if (ocean_scacs) {
      ocean_scacs.OCEAN_SCAC_CODES = [
        { carrier: t('SELECT_ONE'), ocean_scac: 'select' },
      ].concat(ocean_scacs.OCEAN_SCAC_CODES);
      this.setState({
        ocean_scacs: ocean_scacs.OCEAN_SCAC_CODES,
      });
    }
  }

  async retrieveRailroads() {
    // This would typically fetch from an API, but for now we'll use sample data
    const railroadItems = [
      { label: 'BNSF', value: 'BNSF' },
      { label: 'CN', value: 'CN' },
      { label: 'CPKC', value: 'CPKC' },
      { label: 'CSX', value: 'CSX' },
      { label: 'FCCM', value: 'FCCM' },
      { label: 'FEC', value: 'FEC' },
      { label: 'FSR', value: 'FSR' },
      { label: 'FXE', value: 'FXE' },
      { label: 'NDM', value: 'NDM' },
      { label: 'NS', value: 'NS' },
      { label: 'UP', value: 'UP' },
      { label: 'UTFM', value: 'UTFM' },
    ];
    this.setState({ railroadItems });
  }

  componentDidMount() {
    OrderCreateStore.addChangeListener(this.handleOrderCreateStoreChange);
    EquipmentStore.addChangeListener(this.handleEquipmentStoreChange);
    OrdersStore.addSearchResultsChangeListener(
      this.handleOrderSearchResultsChange,
    );
    OrderTemplatesStore.addChangeListener(this.handleOrderTemplatesStoreChange);
    this.retrieveOceanScacs();
    this.retrieveRailroads();
  }

  componentWillUnmount() {
    OrderCreateStore.removeChangeListener(this.handleOrderCreateStoreChange);
    EquipmentStore.removeChangeListener(this.handleEquipmentStoreChange);
    OrdersStore.removeSearchResultsChangeListener(
      this.handleOrderSearchResultsChange,
    );
    OrderTemplatesStore.removeChangeListener(
      this.handleOrderTemplatesStoreChange,
    );
  }

  handleToggleLegManagementVersion = (checked) => {
    this.setState({ leg_management_version: checked ? 2 : 1 });
  };

  handleClearErrors = () => {
    this.setState({ validationErrors: {} });
  };

  handleToggleSaveAs = () =>
    this.setState({
      saveAsToggled: true,
    });

  handleTemplateSelect = async (e) => {
    const {
      target: { value: templateId },
    } = e;

    let template;
    if (templateId) {
      // fetch order template
      try {
        template = await getOrderTemplate(templateId);
      } catch (e) {
        console.error(e);
      }
      const order = applyTemplate(template);

      if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
        const shipper = template?.shipper || null;
        const shipperId = shipper?._id || '';
        if (shipperId) {
          this.getShipperReferenceNumberRequirements(shipperId);
        }
        this.setState({
          ...order,
          templateId,
          shipper: {
            _id: shipperId,
            company_name: shipper?.company_name || '',
            name: shipper?.company_name || '', // I am not happy about the inconsistency in property naming...
          },
        });
      }
      this.setState({
        ...order,
        templateId,
      });
    }
  };

  handleTemplateNameChange = (e) => {
    this.setState((prevState) => {
      return {
        ...prevState,
        name: e,
      };
    });
  };

  handleEquipmentStoreChange = () => {
    const equipmentList = EquipmentStore.getEquipment();
    if (!equipmentList || equipmentList.length === 0) {
      return;
    }

    const equipments = this.state.equipments.map((obj) => {
      const existingEquipment =
        equipmentList.find((o) => o.equipment_id === obj.equipment_id) || obj;
      return {
        ...existingEquipment,
        equipment_uuid: uuid(),
      };
    });
    this.setState((prevState) => {
      return {
        ...prevState,
        equipments,
      };
    });
  };

  handleOrderTemplatesStoreChange = () => {
    const templates = _.sortBy(OrderTemplatesStore.getTemplates(), (template) =>
      (template.name || '').toLowerCase(),
    );
    this.setState((prevState) => {
      return {
        ...prevState,
        templates,
        templateFetchPending: false,
      };
    });
  };

  handleOrderCreateStoreChange = () => {
    const orderCreateState = OrderCreateStore.getCreateOrderState();

    if (orderCreateState.pending) {
      this.setState({
        orderPending: true,
      });
    } else if (orderCreateState.order) {
      const order = orderCreateState.order;

      this.setState(
        {
          orderPending: false,
        },
        () => {
          setTimeout(() => {
            window.location.href = `/orders/${order._id}/delivery-order`;
          }, 2000);
        },
      );
    } else {
      this.setState({
        orderPending: false,
        orderCreateState,
      });
    }
  };

  handleDocumentCustomValueChange = (i, value) => {
    const { documentsPendingUpload: currentDocuments } = this.state;
    const updatedDocument = { ...currentDocuments[i] };
    updatedDocument.value = value;
    const documentsPendingUpload = [
      ...currentDocuments.slice(0, i),
      updatedDocument,
      ...currentDocuments.slice(i + 1),
    ];
    this.setState({
      documentsPendingUpload,
    });
  };

  handleDocumentTypeChange = (i, docType) => {
    const { documentsPendingUpload: currentDocuments } = this.state;

    const updatedDocument = { ...currentDocuments[i] };
    updatedDocument.type = docType;
    const documentsPendingUpload = [
      ...currentDocuments.slice(0, i),
      updatedDocument,
      ...currentDocuments.slice(i + 1),
    ];

    this.setState({
      documentsPendingUpload,
    });
  };
  handleClose = () => {
    this.setState(getInitialState(this.props.localeData));
    this.props.onClose();
  };
  handleShipperFieldChange = () => {
    this.setState({
      shipper: {
        name: '',
        _id: null,
      },
      carrierPreferences: {
        preferred: [],
        excluded: [],
      },
    });
  };
  handleShipperSelect = (item, value) => {
    this.setState(
      {
        shipper: {
          name: value.name,
          _id: value._id,
        },
        accountingHold: value.accounting ? value.accounting.on_hold : false,
        templateFetchPending: true,
      },
      () => OrdersActionCreators.getOrderTemplates({ shipperId: value._id }),
    );
    if (featureEnabled(CONFIGURED_FEATURES.SSR)) {
      this.getShipperReferenceNumberRequirements(value._id);
    }
    this.getCarrierPreferences(value._id);
  };

  getShipperReferenceNumberRequirements = async (shipperId) => {
    const response = await getReferenceNumberRequirements(shipperId);
    const requiredRefNos = (
      response?.data?.getReferenceNumberRequirementsQuery || []
    ).map((refNums) => {
      return {
        ...refNums,
        reference_number: refNums.reference_number.slug,
      };
    });

    this.setShipperReferenceNumbers(requiredRefNos);
  };

  setShipperReferenceNumbers = (refNos) => {
    let { requiredAtBooking, optional } = refNos.reduce(
      (acc, refNo) => {
        if (refNo.required_at === 'BOOKING_TIME') {
          acc.requiredAtBooking.push(refNo);
        } else {
          acc.optional.push(refNo);
        }
        return acc;
      },
      { requiredAtBooking: [], optional: [] },
    );
    optional = optional.filter(
      (refNo) => !EXCLUDED_REFERENCE_NUMBERS.includes(refNo.reference_number),
    );

    const requiredReferenceNumbers =
      mapOrderMasterReferenceBookingTimeRequirement(
        requiredAtBooking,
        this.state.order_type,
      ).reduce((obj, refNo) => {
        obj[refNo.reference_number] = {
          name: refNo.reference_number,
          value: '',
          hidden: false,
          editable: true,
        };
        return obj;
      }, {});

    this.setState({
      requiredAtBooking,
      optional,
      requiredReferenceNumbers,
    });
  };

  getCarrierPreferences = async (shipperId) => {
    const carrierPreferences = await getCarrierPreferencesByType(
      shipperId,
      'drayage',
    );
    this.setState({ carrierPreferences });
  };

  handleCarrierFieldChange = () => {
    this.setState({
      carrier: {
        name: '',
        _id: null,
      },
    });
  };
  handleCarrierSelect = (item, value) => {
    if (value.is_broker) {
      // Change state to show Alert
    }
    this.setState({
      carrier: {
        name: value.name,
        _id: value._id,
        status: value.status,
      },
      showBrokerCarrierWarning: value.is_broker,
    });
  };
  handleRemoveReferenceNumber = (i) => () => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumbers = [
      ...currentReferenceNumbers.slice(0, i),
      ...currentReferenceNumbers.slice(i + 1),
    ];
    this.setState({
      referenceNumbers,
    });
  };
  handleReferenceNumberFieldChange = (i) => (field, value) => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumber = { ...currentReferenceNumbers[i] };
    referenceNumber[field] = value;
    const referenceNumbers = [
      ...currentReferenceNumbers.slice(0, i),
      referenceNumber,
      ...currentReferenceNumbers.slice(i + 1),
    ];

    this.setState({
      referenceNumbers,
    });
  };

  handleAddReferenceNumber = () => {
    const { referenceNumbers: currentReferenceNumbers } = this.state;

    const referenceNumbers = [...currentReferenceNumbers, addReferenceNumber()];

    this.setState({
      referenceNumbers,
    });
  };

  handleOrderTypeChange = (e) => {
    const order_type = e.target.value;
    let movement_type = this.state.movement_type;
    let originating_location = this.state.originatingLocation;
    let mode = this.state.mode;

    const arriving = _.cloneDeep(this.state.arriving);
    const delivering = _.cloneDeep(this.state.delivering);

    if (order_type === DOMESTIC) {
      originating_location = UltimateDestination;
      mode = INTERMODAL;
      arriving.location_type = CUSTOMER;
      delivering.location_type = CUSTOMER;
    } else if (order_type === EXPORT) {
      arriving.location_type = CUSTOMER;
      delivering.location_type = TERMINAL;
      movement_type = PICKUP;
      originating_location = FinalDestination;
      mode = DRAYAGE;
    } else if (order_type === IMPORT) {
      arriving.location_type = TERMINAL;
      delivering.location_type = CUSTOMER;
      movement_type = DELIVERY;
      originating_location = OverseasPort;
      mode = DRAYAGE;
    } else if (order_type === REPOSITION) {
      // If you have specific logic for REPOSITION, add here. Otherwise, default to drayage.
      mode = DRAYAGE;
    }

    const updates = {
      order_type,
      arriving,
      delivering,
      movement_type,
      originating_location,
      mode,
    };

    this.setState(updates, async () => {
      if (featureEnabled(CONFIGURED_FEATURES.SSR) && this.state.shipper?._id) {
        const shipperId = this.state.shipper?._id || this.state.shipper;
        await this.getShipperReferenceNumberRequirements(shipperId);
      }
    });
  };

  handleMovementTypeChange = (e) => {
    const movement_type = e.target.value;

    this.setState((prevState) => {
      const {
        arriving: currentArriving,
        delivering: currentDelivering,
        order_type,
      } = prevState;

      const arriving = _.cloneDeep(currentArriving);
      const delivering = _.cloneDeep(currentDelivering);
      if (movement_type === DELIVERY && order_type === DOMESTIC) {
        return {
          arriving: {
            ...arriving,
            location_type: TERMINAL,
          },
          delivering: {
            ...delivering,
            location_type: CUSTOMER,
          },
          movement_type,
        };
      }
      if (movement_type === DELIVERY && order_type !== DOMESTIC) {
        return {
          arriving: {
            ...arriving,
            location_type: CUSTOMER,
          },
          delivering: {
            ...delivering,
            location_type: TERMINAL,
          },
          movement_type,
        };
      }

      if (order_type === DOMESTIC) {
        return {
          arriving: {
            ...arriving,
            location_type: CUSTOMER,
          },
          delivering: {
            ...delivering,
            location_type: TERMINAL,
          },
          movement_type,
        };
      }

      return {
        arriving: {
          ...arriving,
          location_type: TERMINAL,
        },
        delivering: {
          ...delivering,
          location_type: CUSTOMER,
        },
        movement_type,
      };
    });
  };

  handleHazmatCheckboxChange = (e) => {
    const hazardous = Boolean(e.target && e.target.checked);
    if (hazardous) {
      if (featureEnabled(CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS)) {
        this.setState({
          hazardous,
          hazmat_details: [hazmatInitState],
        });
      } else {
        this.setState({
          hazardous,
          ...hazmatInitState,
        });
      }
    } else {
      this.setState({ hazardous, hazmat_details: [] });
    }
  };

  handleFieldChange = (field) => (e) =>
    this.setState({ [field]: e.target.value });

  handleHazmatFieldChange = (field, index) => (e) => {
    const value = e.target.value;
    this.setState((prev) => {
      const hazmatDetails = prev.hazmat_details || [];
      const hazmatDetail = hazmatDetails[index];
      const firstArray = hazmatDetails.slice(0, index);
      const secondArray = hazmatDetails.slice(index + 1);
      const updatedHazmat = _.assign({}, hazmatDetail, {
        [field]: value,
      });
      return {
        ...prev,
        hazmat_details: [...firstArray, updatedHazmat, ...secondArray],
      };
    });
  };

  handleAddHazmat = () => {
    this.setState((prev) => {
      return {
        ...prev,
        hazmat_details: [...prev.hazmat_details, hazmatInitState],
      };
    });
  };

  handleRemoveHazmat = (index) => {
    this.setState((prev) => {
      const hazmatDetails = prev.hazmat_details;
      return {
        ...prev,
        hazmat_details: [
          ...hazmatDetails.slice(0, index),
          ...hazmatDetails.slice(index + 1),
        ],
      };
    });
  };

  handleCargomaticComponentFieldChange = (field, value) => {
    this.setState({ [field]: value });
  };

  handleDateFieldChange = (field) => (value) =>
    this.setState({ [field]: value });

  handleDrop = (files) => {
    if (files.length > 0) {
      const { documentsPendingUpload: currentDocsPendingUpload } = this.state;

      const newDocuments = files.map((file) => ({
        file,
        documentKey: getRandomKey(),
        type: '',
      }));

      const documentsPendingUpload = [
        ...currentDocsPendingUpload,
        ...newDocuments,
      ];
      this.setState({
        documentsPendingUpload,
      });
    }
  };

  handleRemoveDocument = (i) => {
    const { documentsPendingUpload: currentDocuments } = this.state;

    const documentsPendingUpload = [
      ...currentDocuments.slice(0, i),
      ...currentDocuments.slice(i + 1),
    ];

    this.setState({ documentsPendingUpload });
  };

  handleToggleAutocreateShipments = () =>
    this.setState((state) => ({
      autocreate_shipments: !state.autocreate_shipments,
    }));

  handleCreateOrderAndTemplate = async () => {
    this.validateEquipments();
    const { validationErrors, documentsPendingUpload } = await this.validate(
      this.state,
    );
    this.setState(
      () => {
        return {
          validationErrors,
          documentsPendingUpload,
        };
      },
      async () => {
        const { validationErrors, name } = this.state;
        if (name && Object.keys(validationErrors).length === 0) {
          const order = this.transformOrder();

          const documentIds = await this.handleUploadDocuments(order);

          if (documentIds) {
            if (this.state.templateId) {
              OrdersActionCreators.saveTemplateAndBookOrder(
                this.state.templateId,
                this.state.name,
                order,
                documentIds,
              );
            } else {
              OrdersActionCreators.createOrderAndTemplate(
                this.state.name,
                order,
                documentIds,
              );
            }
          }
        } else {
          this.errorRef.current.scrollIntoView();
        }
      },
    );
  };

  validateEquipments = () => {
    const { equipments } = this.state;
    let equipmentValidationSuccess = true;
    const equipmentsValidated = equipments.map((equipment) => {
      if (!equipment._equipmentType || equipment._equipmentType === '') {
        equipment._isEquipmentTypeValid = false;
        equipmentValidationSuccess = false;
      }
      if (equipment._equipmentType === 'container') {
        if (!equipment._equipmentSize || equipment._equipmentSize === '') {
          equipment._isEquipmentSizeValid = false;
          equipmentValidationSuccess = false;
        }
        if (!equipment._type || equipment._type === '') {
          equipment._isTypeValid = false;
          equipmentValidationSuccess = false;
        }
      }
      if (equipment._equipmentType === 'trailer') {
        if (!equipment._equipmentSize || equipment._equipmentSize === '') {
          equipment._isEquipmentSizeValid = false;
          equipmentValidationSuccess = false;
        }
      }
      equipment._chassisOwnerType = true;
      equipment._chassisOwner = true;
      const isChassisPrivate =
        equipment._equipmentType === 'chassis' && equipment.is_private;
      if (isChassisPrivate) {
        if (!equipment.chassis_owner_type) {
          equipment._chassisOwnerType = false;
          equipmentValidationSuccess = false;
        }

        const isChassisOwnerRequired =
          equipment?.chassis_owner_type === 'customer' &&
          !equipment?.chassis_owner;
        if (isChassisOwnerRequired) {
          equipment._chassisOwner = false;
          equipmentValidationSuccess = false;
        }
      }
      if (equipment._equipmentType !== 'chassis') {
        equipment.is_private = false;
        equipment.chassis_owner_type = null;
        equipment.chassis_owner = null;
      }
      const hasNotUniqueId =
        equipments.filter((e) => e.equipment_id === equipment.equipment_id)
          ?.length > 1;
      if (hasNotUniqueId) {
        equipmentValidationSuccess = false;
      }
      return equipment;
    });
    return { equipmentsValidated, equipmentValidationSuccess };
  };

  handleCreateOrder = async () => {
    const { equipmentsValidated, equipmentValidationSuccess } =
      this.validateEquipments();
    const { validationErrors, documentsPendingUpload } = await this.validate(
      this.state,
    );
    this.setState(
      (prevState) => {
        if (!equipmentValidationSuccess) {
          validationErrors.equipments = 'FILL_EQUIPMENT_DETAILS';
        }
        return {
          ...prevState,
          equipments: equipmentsValidated,
          validationErrors,
          documentsPendingUpload,
        };
      },
      async () => {
        const { validationErrors } = this.state;
        if (Object.keys(validationErrors).length === 0) {
          const order = this.transformOrder();

          const documentIds = await this.handleUploadDocuments(order);

          if (documentIds) {
            OrdersActionCreators.createOrder(order, documentIds);
          }
        } else {
          this.errorRef.current.scrollIntoView();
        }
      },
    );
  };

  handleUploadDocuments = async (order) => {
    if (!order.documentsPendingUpload || !order.documentsPendingUpload.length) {
      return [];
    }

    const { t } = this.props;

    this.setState({
      uploadPending: true,
    });

    const data = await Promise.all(
      order.documentsPendingUpload.map(async (document) => {
        return {
          type: document.type,
          content: document.file,
          contentType: document.file.type,
          name: orderDocTypesMap[document.type]?.name ?? document.type,
          size: document.size,
        };
      }),
    );

    const { data: uploadData, error: uploadError } =
      await uploadDocuments(data);

    this.setState({
      uploadPending: false,
    });

    if (uploadError) {
      const standardErrorSlug = t('DOCUMENT_UPLOAD_FAILED_WITH_ERROR', {
        uploadError,
      });
      const limitErrorSlug = t('FILE_SIZE_TOO_BIG', { MB: uploadError._limit });
      const slugError = uploadError._limit ? limitErrorSlug : standardErrorSlug;

      showToast(t('DOCUMENT_UPLOAD_FAILED_RETRY'), slugError, 'error');
      return false;
    }

    const documentIds = uploadData.uploadDocuments.map(
      (document) => document.id,
    );

    delete order.documentsPendingUpload;

    return documentIds;
  };

  handleOrderSearchResultsChange = () => {
    const results = OrdersStore.getOrdersSearchResults();
    const masterReferenceType = getOrderMasterReferenceType(
      this.state.order_type,
    );

    const duplicateMasterRefExists = results.some(
      (o) =>
        o.reference_numbers.filter(
          (r) =>
            r.name === masterReferenceType &&
            r.optimized ===
              this.state.requiredReferenceNumbers?.master_bol?.value
                .trim()
                .toUpperCase(),
        ).length > 0,
    );

    this.setState({ duplicateMasterRefExists });
  };

  handleOriginatingAddressSelect = (item) => {
    const { lat, lng, address, name, place_id } = item;
    const coords = [lng, lat];

    const currentOriginatingAddress = _.cloneDeep(this.state.originating);

    const originating = Object.assign(
      { ...currentOriginatingAddress },
      {
        location: {
          name,
          address,
          lat,
          lng,
          coords,
          place_id,
        },
      },
    );
    this.setState({ originating });
  };

  handleOriginDetailsFieldChange = (field, value) => {
    this.handleLocationDetailsFieldChange('arriving', field, value);
  };

  handleOriginDetailsContactFieldChange = (i, field, value) => {
    this.handleLocationDetailsContactFieldChange('arriving', i, field, value);
  };

  handleOriginDetailsAddContact = () =>
    this.handleLocationDetailsAddContact('arriving');
  handleOriginDetailsRemoveContact = (i) =>
    this.handleLocationDetailsRemoveContact('arriving', i);

  handleDestinationDetailsFieldChange = (field, value) => {
    this.handleLocationDetailsFieldChange('delivering', field, value);
  };

  handleDestinationDetailsContactFieldChange = (i, field, value) => {
    this.handleLocationDetailsContactFieldChange('delivering', i, field, value);
  };

  handleDestinationDetailsAddContact = () =>
    this.handleLocationDetailsAddContact('delivering');
  handleDestinationDetailsRemoveContact = (i) =>
    this.handleLocationDetailsRemoveContact('delivering', i);

  handleLocationDetailsAddContact = (field) => {
    const details = this.state[field] || {};
    const contacts = [...(details.contacts || []), addContact()];
    const updatedLocationDetails = _.cloneDeep(details);
    updatedLocationDetails.contacts = contacts;

    this.setState({
      [field]: updatedLocationDetails,
    });
  };
  handleLocationDetailsRemoveContact = (field, index) => {
    const details = this.state[field] || {};
    const contacts = [
      ...details.contacts.slice(0, index),
      ...details.contacts.slice(index + 1),
    ];
    const updatedLocationDetails = _.cloneDeep(details);
    updatedLocationDetails.contacts = contacts;

    this.setState({
      [field]: updatedLocationDetails,
    });
  };
  handleLocationDetailsContactFieldChange = (
    field,
    index,
    formField,
    value,
  ) => {
    const details = this.state[field] || {};
    const contact = { ...details.contacts[index] };
    contact[formField] = value;
    contact[field] = value;
    const contacts = [
      ...details.contacts.slice(0, index),
      contact,
      ...details.contacts.slice(index + 1),
    ];
    const updatedLocationDetails = _.cloneDeep(details);
    updatedLocationDetails.contacts = contacts;

    this.setState({
      [field]: updatedLocationDetails,
    });
  };

  handleLocationDetailsFieldChange = (field, formField, value) => {
    const details = this.state[field] || {};
    const updatedLocationDetails = _.cloneDeep(details);
    updatedLocationDetails[formField] = value;
    if (formField === 'name') {
      updatedLocationDetails.location.name = value;
    }
    this.setState({
      [field]: updatedLocationDetails,
    });
  };

  transformLocation(data) {
    const {
      location_type,
      name,
      location: { address, coords, lat, lng, place_id },
      contacts,
    } = data;

    if (!address) {
      return null;
    }

    const location = {
      address,
      coords,
      lat,
      lng,
      name,
      location_type,
      place_id,
    };

    if (contacts && contacts.length > 0) {
      location.contact = contacts[0];
    }

    return [location];
  }

  async validate(order) {
    const validationErrors = {};
    if (!order.shipper._id) {
      validationErrors.shipper = 'SELECT_SHIPPER';
    }

    if (featureEnabled(CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS)) {
      if (
        order.hazardous &&
        order.hazmat_details?.length > 1 &&
        order.hazmat_details.length >
          _.uniqBy(order.hazmat_details, (hazmat) => {
            return `class_${hazmat.hazmat_class}_package_${hazmat.hazmat_packaging_group}_description${hazmat.hazmat_description}`;
          }).length
      ) {
        validationErrors.hazmat_details = 'HAZMAT_DETAILS_ARE_DUPLICATED';
      }
      if (
        order.hazardous &&
        order.hazmat_details?.length === 1 &&
        order.hazmat_details[0].hazmat_class === 'N/A' &&
        order.hazmat_details[0].hazmat_packaging_group === 'N/A' &&
        order.hazmat_details[0].hazmat_description?.trim() === ''
      ) {
        validationErrors.hazmat_details = 'HAZMAT_DETAILS_ARE_NOT_PROVIDED';
      }
    }

    if (order.carrier._id && order.carrier.status !== 'Trained') {
      validationErrors.carrier = 'CARRIER_IS_NOT_TRAINED';
    }

    const { master_bol, requiredReferenceNumbers, order_type } = order;
    if (!master_bol && !featureEnabled(CONFIGURED_FEATURES.SSR)) {
      if (order_type === EXPORT) {
        validationErrors.master_bol = 'ENTER_BOOKING_NUMBER';
      } else {
        validationErrors.master_bol = 'ENTER_MASTER_REFERENCE_NUMBER';
      }
    }

    if (
      Object.values(requiredReferenceNumbers).find(
        (refNo) => !refNo.name || !refNo.value,
      )
    ) {
      validationErrors.required_reference_numbers =
        'MISSING_REQUIRED_REFERENCE_NUMBER';
    }

    if (
      order.order_type !== DOMESTIC &&
      (!order.ocean_carrier_scac || order.ocean_carrier_scac === 'select')
    ) {
      validationErrors.ocean_carrier_scac = 'SELECT_OCEAN_CARRIER_SCAC';
    }

    // Validate railroads if needed
    if (
      order.order_type === DOMESTIC &&
      order.mode === INTERMODAL &&
      !order?.railroads?.length
    ) {
      validationErrors.railroads = 'SELECT_RAILROAD';
    }

    if (
      !order.custom_ocean_carrier_scac &&
      order.ocean_carrier_scac === 'Custom'
    ) {
      validationErrors.ocean_carrier_scac = 'ENTER_CUSTOM_OCEAN_CARRIER_SCAC';
    }

    if (
      order.custom_ocean_carrier_scac &&
      order.ocean_carrier_scac === 'Custom'
    ) {
      if (order.custom_ocean_carrier_scac.length !== 4) {
        validationErrors.ocean_carrier_scac =
          'CUSTOM_OCEAN_CARRIER_SCAC_MUST_BE_FOUR_CHARACTERS';
      }
    }

    const { arriving, delivering } = order;
    if (!arriving.location.address) {
      validationErrors.arriving = 'ENTER_ORIGIN_ADDRESS';
    }

    if (!delivering.location.address) {
      validationErrors.delivering = 'ENTER_DESTINATION_ADDRESS';
    }

    const missingDocumentTypes = order.documentsPendingUpload.find(
      (d) => !d.type || d.type.length === 0,
    );
    const documentsPendingUpload = order.documentsPendingUpload.map((doc) => {
      const error = !doc.type || doc.type.length === 0;
      return {
        ...doc,
        error,
      };
    });

    if (missingDocumentTypes) {
      validationErrors.documents = 'SET_DOCUMENT_TYPE';
    }

    const { referenceNumbers } = order;

    for (let i = 0; i < referenceNumbers.length; i += 1) {
      const refNum = referenceNumbers[i];
      if (!refNum.name || !refNum.value) {
        validationErrors.reference_numbers = 'REFERENCE_NUMBER_CANT_BE_EMPTY';
        break;
      }
    }
    const { entity: validatedEquipmentData } = await checkEquipmentExistsInBulk(
      this.state.equipments,
    );
    validatedEquipmentData.result.forEach((equipmentItem) => {
      if (equipmentItem.err) {
        validationErrors.equipment_mismatch = {
          ...validationErrors.equipment_mismatch,
          [equipmentItem.equipment.equipment_id]: equipmentItem.err,
        };
      }
    });
    return {
      validationErrors,
      documentsPendingUpload,
    };
  }

  transformOrder() {
    const {
      shipper: { _id: shipper },
      documentsPendingUpload: currentDocuments,
      cargo_description,
      order_received_date,
      service_type,
      reefer_temperature,
      reefer_temperature_unit,
      count,
      certified_weight,
      vessel_or_rail,
      train,
      railroads,
      ocean_carrier_scac,
      custom_ocean_carrier_scac,
      estimated_arrival,
      arrival_date,
      earliest_empty,
      earliest_return_date,
      special_instructions,
      beneficial_cargo_owner,
      master_bol,
      delivery_order,
      pu_number,
      referenceNumbers,
      last_free_date,
      order_type,
      mode,
      total_weight,
      weight_units,
      movement_type,
      hazmat_class,
      hazmat_packaging_group,
      hazmat_description,
      hazmat_details,
      autocreate_shipments,
      requiredReferenceNumbers,
      leg_management_version,
    } = this.state;

    const carrier = this.state.carrier._id || null;
    const originating = this.transformLocation(this.state.originating);
    const arriving = this.transformLocation(this.state.arriving);
    const delivering = this.transformLocation(this.state.delivering);
    const selectedRailRoads =
      railroads?.length > 0 ? railroads.map(({ value }) => value) : [];

    const reference_numbers = [];
    if (master_bol) {
      reference_numbers.push({
        name: getOrderMasterReferenceType(order_type),
        value: master_bol,
        optimized: master_bol.toUpperCase(),
      });
    }

    if (delivery_order) {
      reference_numbers.push({
        name: 'delivery_order',
        value: delivery_order,
        optimized: delivery_order.toUpperCase(),
      });
    }

    if (pu_number) {
      reference_numbers.push({
        name: 'pu_number',
        value: pu_number,
        optimized: pu_number.toUpperCase(),
      });
    }

    referenceNumbers.push(
      ...Object.keys(requiredReferenceNumbers).map((r) => {
        const refNo = requiredReferenceNumbers[r];
        return {
          ...refNo,
          optimized: refNo.value.toUpperCase(),
        };
      }),
    );

    reference_numbers.push(
      ...referenceNumbers.map((r) => ({
        ...r,
        optimized: r.value.toUpperCase(),
      })),
    );

    const documentsPendingUpload = currentDocuments.map((doc) => ({
      ...doc,
      type: doc.type === 'custom' ? doc.value : doc.type,
    }));

    const equipments = this.state.equipments
      .filter((e) => e.equipment_id !== '')
      .map((_equipment) => {
        if (_equipment.is_private) {
          const isChassisOwnerPresent =
            _equipment.chassis_owner_type === 'customer' &&
            _equipment?.chassis_owner?._id;
          _equipment.chassis_owner = isChassisOwnerPresent
            ? _equipment.chassis_owner._id
            : null;
        }
        return _equipment;
      });

    return {
      hazmat_class,
      hazmat_packaging_group,
      hazmat_description,
      hazmat_details,
      shipper,
      carrier,
      order_received_date,
      vessel_or_rail,
      train,
      railroads: selectedRailRoads,
      ocean_carrier_scac,
      custom_ocean_carrier_scac,
      originating,
      arriving,
      delivering,
      service_type,
      movement_type,
      estimated_arrival,
      arrival_date,
      earliest_return_date,
      earliest_empty,
      special_instructions,
      cargo_description,
      beneficial_cargo_owner,
      reference_numbers,
      documentsPendingUpload,
      last_free_date,
      order_type,
      mode,
      certified_weight,
      count,
      reefer_temperature,
      reefer_temperature_unit,
      total_weight,
      weight_units,
      equipments,
      autocreate_shipments,
      leg_management_version,
    };
  }

  handleReferenceNumberSearch(value) {
    const query = {
      reference: [value],
      paginate: true,
      per_page: 1,
      page: 1,
      count: false,
    };
    OrdersActionCreators.getOrdersByQuery(query);
  }

  debouncedReferenceNumberSearch = _.debounce(
    this.handleReferenceNumberSearch,
    500,
  );

  getDebouncedFunction = (refNum) => {
    if (!this.debouncedFunctions[refNum]) {
      this.debouncedFunctions[refNum] = _.debounce(
        this.handleReferenceNumberSearch,
        500,
      );
    }
    return this.debouncedFunctions[refNum];
  };

  handleRequiredReferenceNumberFieldChange = (e, field, refSearch = false) => {
    const value = e.target.value;
    this.setState((prev) => {
      const { requiredReferenceNumbers } = prev;
      requiredReferenceNumbers[field].value = value;
      return { ...prev, requiredReferenceNumbers };
    });
    if (refSearch) {
      if (value.trim().length > 2) {
        const debouncedSearch = this.getDebouncedFunction(field);
        debouncedSearch(value.trim());
      }
    }
  };

  handleEquipmentRemove(equipment) {
    const equipments = this.state.equipments.filter((eq) => {
      return eq.equipment_uuid !== equipment.equipment_uuid;
    });
    this.setState((prevState) => {
      return {
        ...prevState,
        equipments,
      };
    });
  }

  handleEquipmentChange(equipment, key, value) {
    const equipments = this.state.equipments;
    const newEquipments = equipments.map((eq) => {
      if (eq.equipment_uuid === equipment.equipment_uuid) {
        if (key === '_equipmentType' && value !== eq[key]) {
          eq._type = '';
          eq._equipmentSize = '';
          eq._isEquipmentSizeValid = true;
          eq._isTypeValid = true;
          eq._isEquipmentTypeValid = true;
        }

        const isPrivateChassisAttributesResetRequired =
          (key === '_equipmentType' && value !== 'chassis') ||
          (key === 'is_private' && value === false);

        if (isPrivateChassisAttributesResetRequired) {
          eq.is_private = false;
          eq.chassis_owner_type = null;
          eq.chassis_owner = null;
          eq._chassisOwnerType = true;
          eq._chassisOwner = true;
        }

        const isChassisOwnerTypeUpdating =
          eq.is_private && key === 'chassis_owner_type';
        if (isChassisOwnerTypeUpdating) {
          eq._chassisOwnerType = true;
        }

        const isChassisOwnerUpdating =
          eq.is_private &&
          eq.chassis_owner_type === 'customer' &&
          key === 'chassis_owner';
        if (isChassisOwnerUpdating) {
          eq._chassisOwner = true;
        }

        if (key === '_equipmentType') {
          eq._isEquipmentTypeValid = true;
        }
        if (key === '_equipmentSize') {
          eq._isEquipmentSizeValid = true;
        }
        if (key === '_type') {
          eq._isTypeValid = true;
        }
        eq[key] = value;
      }
      return eq;
    });

    this.setState({ equipments: newEquipments });
  }

  handleEquipmentBulkChange(equipment) {
    const equipments = this.state.equipments;
    const newEquipments = equipments.map((eq) => {
      return {
        ...equipment,
        equipment_id: eq.equipment_id,
        equipment_uuid: eq.equipment_uuid,
      };
    });
    this.setState((prevState) => {
      return {
        ...prevState,
        equipments: newEquipments,
      };
    });
  }

  handleEquipmentAdd() {
    const equipments = this.state.equipments;
    equipments.push({
      equipment_uuid: uuid(),
      equipment_id: '',
      _equipmentType: '',
      _equipmentSize: '',
      _isEquipmentTypeValid: true,
      _isTypeValid: true,
      _isEquipmentSizeValid: true,
    });
    this.setState((prevState) => {
      return {
        ...prevState,
        equipments,
      };
    });
  }

  handleEquipmentBulkAdd() {
    const value = this.state.equipmentsToAdd;

    if (value) {
      const items = convertCommaSeparatedStringToArray(value);
      const equipments = items.map((item) => ({
        equipment_uuid: uuid(),
        equipment_id: item,
        _isEquipmentTypeValid: true,
        _isTypeValid: true,
        _isEquipmentSizeValid: true,
      }));

      if (equipments.length > 0) {
        const params = {
          equipment_id: equipments.map((eq) => eq.equipment_id),
        };

        this.setState({ equipments, equipmentsToAdd: '' }, () => {
          EquipmentActionCreators.getAllEquipment(params);
        });
      }
    }
  }

  handleRailroadsSelect = (selectedRailroads) => {
    this.setState({ railroads: selectedRailroads || [] });
  };

  renderOrderDetails() {
    const { t } = this.props;
    return (
      <section className="mb-8">
        <h4 className="mb-2 text-xl text-grey-dark">{t('ORDER_DETAILS')}</h4>
        <div className="sm:grid sm:grid-cols-3 lg:grid-cols-4 sm:gap-4">
          <fieldset className="">
            <label className="mb-1 form-label">{t('ORDER_TYPE')}</label>
            <select
              id="order-type-selector"
              data-testid="order-type-select"
              onChange={this.handleOrderTypeChange}
              value={this.state.order_type}
              className="w-full mb-4 form-select"
            >
              {orderTypes.map((orderType) => (
                <option key={orderType.value} value={orderType.value}>
                  {t(sluggify(orderType.name))}
                </option>
              ))}
            </select>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{titleize(t('MODE'))}</label>
            <select
              id="mode-selector"
              data-testid="mode-select"
              onChange={this.handleFieldChange('mode')}
              value={this.state.mode}
              className="w-full mb-4 form-select"
            >
              {modes.map((mode) => (
                <option key={mode} value={mode}>
                  {titleize(t(sluggify(mode)))}
                </option>
              ))}
            </select>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('SERVICE_TYPE')}</label>
            <select
              id="order-service-type-selector"
              onChange={this.handleFieldChange('service_type')}
              value={this.state.service_type}
              className="w-full mb-4 form-select"
            >
              {serviceTypes.map((serviceType) => (
                <option key={serviceType.value} value={serviceType.value}>
                  {t(serviceType.name)}
                </option>
              ))}
            </select>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('MOVEMENT_TYPE')}</label>
            <select
              id="order-movement-type-selector"
              onChange={this.handleMovementTypeChange}
              value={this.state.movement_type}
              className="w-full mb-4 form-select"
            >
              {movementTypes.map((movementType) => (
                <option key={movementType.value} value={movementType.value}>
                  {titleize(t(sluggify(movementType.name)))}
                </option>
              ))}
            </select>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">
              {t('ORDER_RECEIVED_DATE')}
            </label>
            <div className="block mb-4">
              <DatePicker
                selected={this.state.order_received_date}
                placeholderText={t('ORDER_RECEIVED_DATE')}
                locale={this.props.localeData.languageCodeId}
                className="w-full form-field"
                onSelect={this.handleDateFieldChange('order_received_date')}
              />
            </div>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('TOTAL_WEIGHT')}</label>
            <div className="block mb-4">
              <input
                id="order-weight"
                type="number"
                className="block w-full mb-4 form-field"
                placeholder={t('TOTAL_WEIGHT')}
                value={this.state.total_weight}
                onChange={this.handleFieldChange('total_weight')}
              />
            </div>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('CERTIFIED_WEIGHT')}</label>
            <div className="block mb-4">
              <input
                id=""
                type="number"
                className="block w-full mb-4 form-field"
                placeholder={t('CERTIFIED_WEIGHT')}
                value={this.state.certified_weight}
                onChange={this.handleFieldChange('certified_weight')}
              />
            </div>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('WEIGHT_UNIT')}</label>
            <select
              id="order-service-type-selector"
              onChange={this.handleFieldChange('weight_units')}
              value={this.state.weight_units}
              className="w-full mb-4 form-select"
              data-testid="weight-unit-dropdown"
            >
              {weightUnitsOptions.map((weightUnit) => (
                <option key={weightUnit.value} value={weightUnit.value}>
                  {t(weightUnit.localizationSlug)}
                </option>
              ))}
            </select>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">{t('REEFER_TEMPERATURE')}</label>
            <div className="block mb-4">
              <input
                id=""
                type="number"
                className="block w-full mb-4 form-field"
                placeholder={t('REEFER_TEMPERATURE')}
                value={this.state.reefer_temperature}
                onChange={this.handleFieldChange('reefer_temperature')}
              />
            </div>
          </fieldset>
          <fieldset className="">
            <div className="block mb-4">
              <CargomaticSelect
                id="order-service-type-selector"
                showCaption
                label={t('REEFER_TEMPERATURE_UNIT')}
                value={this.state.reefer_temperature_unit}
                className="w-full mb-4"
                onSelect={(tempOption) =>
                  this.handleCargomaticComponentFieldChange(
                    'reefer_temperature_unit',
                    tempOption.value,
                  )
                }
                valueKey="value"
                textKey="localizationSlug"
                items={temperatureUnitsOptions}
                labelClassName="mb-1"
              />
            </div>
          </fieldset>
          <fieldset className="">
            <label className="mb-1 form-label">
              {titleize(t('COUNT', { count: 1 }))}
            </label>
            <div className="block mb-4">
              <input
                id=""
                type="number"
                className="block w-full mb-4 form-field"
                placeholder={titleize(t('COUNT', { count: 1 }))}
                value={this.state.count}
                onChange={this.handleFieldChange('count')}
              />
            </div>
          </fieldset>
        </div>
      </section>
    );
  }

  renderAccountingHoldError() {
    const { t } = this.props;
    return (
      <Row>
        <Col xs={12}>
          <OldAlert style={{ marginTop: '12px' }} bsStyle="danger">
            {t('BOOKING_RESTRICTED_FOR_ACCOUNT')}
          </OldAlert>
        </Col>
      </Row>
    );
  }

  renderCustomerDetails() {
    const hasShipperError =
      this.state.validationErrors.hasOwnProperty('shipper');
    const shipperClassName = classnames({
      'md:mr-2': true,
      error: hasShipperError,
    });
    const hasCarrierError =
      this.state.validationErrors.hasOwnProperty('carrier');
    const carrierClassName = classnames({
      'md:mr-2': true,
      error: hasCarrierError,
    });
    const { t } = this.props;
    const shipperName = this.state.shipper ? this.state.shipper.name : '';
    const carrierName = this.state.carrier ? this.state.carrier.name : '';
    const { carrierPreferences } = this.state;

    return (
      <section className="mb-4">
        <h4 className="mb-2 text-xl text-grey-dark">{t('CUSTOMER_DETAILS')}</h4>
        <h6 className="mb-2 text-sm text-grey-dark">
          {t('REQUIRED_FIELD_INDICATOR')}
        </h6>
        <div className="flex flex-wrap">
          <div className="w-full md:w-1/3">
            <fieldset className={shipperClassName}>
              {this.state.accountingHold && this.renderAccountingHoldError()}
              <label className="mb-1 form-label">
                {titleize(t('SHIPPER', { count: 1 }))} *
              </label>
              <div
                id="order-shipper-search-container"
                className="mb-4 customer-search"
              >
                <CustomerSearch
                  id="order-shipper-search"
                  clearOnSelect={false}
                  assignField
                  onChange={this.handleShipperFieldChange}
                  onSelect={this.handleShipperSelect}
                  menuStyle={{
                    position: 'absolute',
                    top: 40,
                    left: 0,
                  }}
                  value={shipperName}
                  placeholder={t('SEARCH_FOR_SHIPPER_PLACEHOLDER')}
                  type="booking_shippers"
                />
              </div>
            </fieldset>
          </div>
          <div className="w-full md:w-1/3">
            <fieldset>
              <label className="mb-1 form-label">
                {t('BENEFICIAL_CARGO_OWNER')}
              </label>
              <input
                id="order-cargo-owner"
                type="text"
                className="w-full mb-4 form-field"
                placeholder={t('BENEFICIAL_CARGO_OWNER')}
                value={this.state.beneficial_cargo_owner}
                onChange={this.handleFieldChange('beneficial_cargo_owner')}
              />
            </fieldset>
          </div>
        </div>
        <div className="flex flex-wrap mb-4">
          <div className="w-full md:w-1/3">
            <fieldset className={carrierClassName}>
              <label className="block mb-1 form-label">
                {titleize(t('CARRIER', { count: 1 }))}
              </label>
              <div
                id="order-carrier-search-container"
                className="mb-4 customer-search"
              >
                <CustomerSearchWithPreferences
                  id="order-carrier-search"
                  clearOnSelect={false}
                  onChange={this.handleCarrierFieldChange}
                  onSelect={this.handleCarrierSelect}
                  placeholder={t('SEARCH_FOR_CARRIER')}
                  defaultValue={carrierName}
                  menuStyle={{
                    position: 'absolute',
                    top: 40,
                    left: 0,
                  }}
                  type="carriers"
                  carrierPreferences={carrierPreferences}
                  isDriver={false}
                />
              </div>
            </fieldset>
            {this.state.showBrokerCarrierWarning && (
              <Alert type="warning">
                <p>{t('ASSIGN_BROKER_CARRIER_WARNING_MESSAGE')}</p>
              </Alert>
            )}
          </div>
        </div>
      </section>
    );
  }

  renderRequiredReferenceNumbers() {
    const { t } = this.props;
    const { requiredAtBooking, order_type } = this.state;
    const masterReferenceType =
      getOrderMasterReferenceType(order_type)?.toUpperCase();
    const sortedReferenceNumbers = featureEnabled(CONFIGURED_FEATURES.SSR)
      ? mapOrderMasterReferenceBookingTimeRequirement(
          requiredAtBooking,
          order_type,
        )
      : [];

    const hasError = this.state.validationErrors.hasOwnProperty('master_bol');
    const masterReferenceClassName = classnames({
      'md:mr-2': true,
      error: hasError,
    });
    return (
      <section>
        <h4 className="mb-2 text-xl text-grey-dark">
          {titleize(t('REFERENCE_NUMBERS'))}
        </h4>
        <div className="flex flex-wrap mb-4">
          {!featureEnabled(CONFIGURED_FEATURES.SSR) ? (
            <>
              <div className="w-full md:w-1/3">
                <fieldset className={masterReferenceClassName}>
                  <div className="flex items-center space-x-1 mb-1">
                    <label className="form-label">
                      {t(masterReferenceType)} *
                    </label>
                    <Tooltip
                      id="tooltip-master-ref"
                      content={t('MASTER_REF_NUM_TIP')}
                      position="top"
                    >
                      <Glyphicon glyph="info-sign" />
                    </Tooltip>
                  </div>
                  <input
                    id="order-master-bol"
                    type="text"
                    className="block w-full mb-4 form-field"
                    placeholder={t(masterReferenceType)}
                    value={this.state.master_bol}
                    onChange={(e) => {
                      const value = e.target.value;
                      this.setState({ master_bol: value });
                      if (value.trim().length > 2) {
                        this.debouncedReferenceNumberSearch(value.trim());
                      }
                    }}
                  />
                  <p className="mt-1 text-xs leading-6 text-blue-500 flex space-x-2 items-start pb-4">
                    <span>{t('MBL_INFO_MESSAGE')}</span>
                  </p>
                  {this.state.duplicateMasterRefExists &&
                    this.renderReferenceNumberError()}
                </fieldset>
              </div>
              <div className="w-full md:w-1/3">
                <fieldset className="md:mr-2">
                  <label className="block mb-1 form-label">
                    {t('DELIVERY_ORDER_NUMBER')}
                  </label>
                  <input
                    id="order-delivery-order"
                    type="text"
                    className="block w-full mb-4 form-field"
                    placeholder={t('DELIVERY_ORDER_NUMBER')}
                    value={this.state.delivery_order}
                    onChange={this.handleFieldChange('delivery_order')}
                  />
                </fieldset>
              </div>
              <div className="w-full md:w-1/3">
                <fieldset>
                  <label className="block mb-1 form-label">
                    {t('PU_NUMBER')}
                  </label>
                  <input
                    id="order-pu-number"
                    type="text"
                    className="block w-full mb-4 form-field"
                    placeholder={t('PU_NUMBER')}
                    value={this.state.pu_number}
                    onChange={this.handleFieldChange('pu_number')}
                  />
                </fieldset>
              </div>
            </>
          ) : (
            <>
              {sortedReferenceNumbers.map((refNo) => {
                if (MASTER_REFERENCE_TYPES.includes(refNo.reference_number)) {
                  return (
                    <div
                      className="w-full md:w-1/3"
                      key={refNo.reference_number}
                    >
                      <fieldset className="md:mr-2 mb-4">
                        <div className="flex items-center space-x-1 mb-1">
                          <label className="form-label">
                            {`${t(masterReferenceType)} *`}
                          </label>
                          <Tooltip
                            id="tooltip-master-ref"
                            content={t('MASTER_REF_NUM_TIP')}
                            position="top"
                          >
                            <Glyphicon glyph="info-sign" />
                          </Tooltip>
                        </div>
                        <input
                          id="order-master-ref"
                          type="text"
                          className="block w-full mb-4 form-field"
                          placeholder={t(masterReferenceType.toUpperCase())}
                          value={
                            this.state.requiredReferenceNumbers[
                              refNo.reference_number
                            ]?.value || ''
                          }
                          onChange={(e) => {
                            this.handleRequiredReferenceNumberFieldChange(
                              e,
                              refNo.reference_number,
                              true,
                            );
                          }}
                        />
                        <p className="mt-1 text-xs leading-6 text-blue-500 flex space-x-2 items-start pb-4">
                          <span>{t('MBL_INFO_MESSAGE')}</span>
                        </p>
                        {this.state.duplicateMasterRefExists &&
                          this.renderReferenceNumberError()}
                      </fieldset>
                    </div>
                  );
                }
                return (
                  <div className="w-full md:w-1/3" key={refNo.reference_number}>
                    <fieldset className="md:mr-2 mb-4">
                      <label className="block mb-1 form-label">
                        {`${t(refNo.reference_number.toUpperCase())}*`}
                      </label>
                      <input
                        id="order-pu-number"
                        type="text"
                        className="block w-full mb-4 form-field"
                        placeholder={t(refNo.reference_number.toUpperCase())}
                        value={
                          this.state.requiredReferenceNumbers[
                            refNo.reference_number
                          ]?.value
                        }
                        onChange={(e) =>
                          this.handleRequiredReferenceNumberFieldChange(
                            e,
                            refNo.reference_number,
                          )
                        }
                      />
                    </fieldset>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </section>
    );
  }

  renderAdditionalReferenceNumbers() {
    const { t } = this.props;
    return (
      <section>
        <h5 className="mt-4 text-grey">{t('ADDITIONAL_REFERENCE_NUMBERS')}</h5>
        <div className="mb-4 reference-number-fields">
          {this.state.referenceNumbers
            .filter((r) => !r.hidden)
            .map((refNum, i) => (
              <ReferenceNumberField
                referenceNumber={refNum}
                key={i}
                onRemove={this.handleRemoveReferenceNumber(i)}
                onChange={this.handleReferenceNumberFieldChange(i)}
                refNumbersList={this.state.optional}
              />
            ))}
        </div>
        <div className="flex mb-4">
          <div className="flex md:w-2/3">
            <button
              className="mb-4 button button-hollow-green"
              onClick={this.handleAddReferenceNumber}
            >
              <i className="mr-2 fa fa-plus" />
              <span>{t('REFERENCE_NUMBER')}</span>
            </button>
          </div>
        </div>
      </section>
    );
  }

  renderReferenceNumbers() {
    if (!this.state.shipper._id) {
      return null;
    }
    return (
      <div className="reference-numbers">
        {this.renderRequiredReferenceNumbers()}
        {this.renderAdditionalReferenceNumbers()}
      </div>
    );
  }

  renderUltimateDestinationField(address, t) {
    return (
      <div id="order-originating-container" className="">
        <fieldset className="" data-testid="ultimate-destination-field">
          <div className="flex items-center space-x-1 mb-1">
            <label className="block inline-block mr-1 form-label">
              {t(sluggify(this.state.originating_location))}
            </label>
            <Tooltip
              id="tooltip-originating-location"
              content={t('ORIGINATING_LOC_TIP')}
              position="top"
            >
              <Info className="w-4 h-4" />
            </Tooltip>
          </div>
          <div className="mb-4">
            <LocationField
              showLabel={false}
              onAddressSelect={this.handleOriginatingAddressSelect}
              address={address}
            />
          </div>
        </fieldset>
      </div>
    );
  }

  renderSteamshipRailroadField() {
    const { t } = this.props;
    const { order_type, ocean_scacs } = this.state;

    const hideSteamShipField = order_type === DOMESTIC;

    const hasSteamshipError =
      this.state.validationErrors.hasOwnProperty('ocean_carrier_scac');
    const steamshipClassName = classnames({
      'md:mr-2': true,
      error: hasSteamshipError,
    });

    if (hideSteamShipField) {
      return null;
    }
    return (
      <fieldset className={steamshipClassName} data-testid="steamship-field">
        <Label className="block mb-1 form-label">{`${t('STEAMSHIP')}*`}</Label>
        {ocean_scacs &&
          ocean_scacs.length &&
          this.state.ocean_carrier_scac === 'Custom' && (
            <div>
              <select
                id="order-service-type-selector"
                onChange={this.handleFieldChange('ocean_carrier_scac')}
                value={this.state.ocean_carrier_scac}
                className="w-full mb-4 form-select"
              >
                {ocean_scacs.map((scac) => (
                  <option key={scac.ocean_scac} value={scac.ocean_scac}>
                    {scac.carrier}
                  </option>
                ))}
              </select>
              <input
                id="order-steamship-rail"
                type="text"
                className="block w-full mb-4 form-field"
                placeholder={t('CUSTOM_OCEAN_CARRIER_SCAS')}
                value={this.state.custom_ocean_carrier_scac}
                onChange={this.handleFieldChange('custom_ocean_carrier_scac')}
              />
            </div>
          )}
        {ocean_scacs &&
          ocean_scacs.length &&
          this.state.ocean_carrier_scac !== 'Custom' && (
            <select
              id="order-service-type-selector"
              onChange={this.handleFieldChange('ocean_carrier_scac')}
              value={this.state.ocean_carrier_scac}
              className="w-full mb-4 form-select"
            >
              {ocean_scacs.map((scac) => (
                <option key={scac.ocean_scac} value={scac.ocean_scac}>
                  {scac.carrier}
                </option>
              ))}
            </select>
          )}
      </fieldset>
    );
  }

  renderRailRoadField() {
    const { t } = this.props;
    const { mode, order_type } = this.state;
    const railRoadRequired = order_type === DOMESTIC && mode === INTERMODAL;
    const hasRailRoadError =
      railRoadRequired &&
      this.state.validationErrors.hasOwnProperty('railroads');

    const railRoadClassName = classnames({
      'md:mr-2': true,
      error: hasRailRoadError,
    });
    return (
      <fieldset className={railRoadClassName} data-testid="railroad-field">
        <Label className="block mb-1 form-label">
          {`${t('RAILROADS')}${railRoadRequired ? '*' : ''}`}
        </Label>
        <MultiSelect
          options={this.state.railroadItems || []}
          value={this.state.railroads || []}
          onChange={this.handleRailroadsSelect}
          placeholder={t('SELECT_RAILROADS')}
          allowCustom={true}
          customOptionLabel={t('CUSTOM_RAILROADS')}
          addCustomLabel={t('ADD_CUSTOM_RAILROAD')}
        />
      </fieldset>
    );
  }

  renderVesselField(vesselClassName) {
    const { t } = this.props;
    return (
      <fieldset className={vesselClassName} data-testid="vessel-field">
        <label className="block mb-1 form-label">{t('VESSEL')}</label>
        <input
          id="order-vessel-train"
          type="text"
          className="block w-full mb-4 form-field"
          placeholder={t('VESSEL')}
          value={this.state.vessel_or_rail}
          onChange={this.handleFieldChange('vessel_or_rail')}
        />
      </fieldset>
    );
  }

  renderTrainField(vesselClassName) {
    const { t } = this.props;
    return (
      <fieldset className={vesselClassName} data-testid="train-field">
        <label className="block mb-1 form-label">
          {_.startCase(t('TRAIN'))}
        </label>
        <input
          id="order-vessel-train"
          type="text"
          className="block w-full mb-4 form-field"
          placeholder={_.startCase(t('TRAIN'))}
          value={this.state.train}
          onChange={this.handleFieldChange('train')}
        />
      </fieldset>
    );
  }

  renderRailwayDetails() {
    const {
      originating: {
        location: { address },
      },
    } = this.state;
    const { t } = this.props;

    const hasVesselError =
      this.state.validationErrors.hasOwnProperty('vessel_or_rail');
    const vesselClassName = classnames({
      'md:mr-2': true,
      error: hasVesselError,
    });

    const shouldHideVesselTrainUltimateDestination =
      this.state.order_type === DOMESTIC;

    return (
      <section className="mb-4">
        <h4 className="mb-4 text-grey-dark">{t('VESSEL_TRAIN')}</h4>
        <div className="lg:grid lg:grid-cols-4 lg:gap-4">
          {!shouldHideVesselTrainUltimateDestination &&
            this.renderUltimateDestinationField(address, t)}

          {/* 2. Steamship/Railroad* (always rendered*/}
          {this.renderSteamshipRailroadField()}

          {this.renderRailRoadField()}

          {!shouldHideVesselTrainUltimateDestination &&
            this.renderVesselField(vesselClassName)}

          {!shouldHideVesselTrainUltimateDestination &&
            this.renderTrainField(vesselClassName)}

          {this.state.order_type === IMPORT && (
            <fieldset className="">
              <label className="block mb-1 form-label">
                {t('ESTIMATED_ARRIVAL_DATE')}
              </label>
              <div className="block mb-4">
                <DatePicker
                  className="block w-full form-field"
                  placeholderText={t('ESTIMATED_ARRIVAL_DATE')}
                  locale={this.props.localeData.languageCodeId}
                  selected={this.state.estimated_arrival}
                  onSelect={this.handleDateFieldChange('estimated_arrival')}
                />
              </div>
            </fieldset>
          )}
          {this.state.order_type === IMPORT && (
            <fieldset className="">
              <label className="block mb-1 form-label">
                {t('ARRIVAL_DATE')}
              </label>
              <div className="block mb-4">
                <DatePicker
                  className="block w-full form-field"
                  placeholderText={t('ARRIVAL_DATE')}
                  locale={this.props.localeData.languageCodeId}
                  selected={this.state.arrival_date}
                  onSelect={this.handleDateFieldChange('arrival_date')}
                />
              </div>
            </fieldset>
          )}
        </div>
      </section>
    );
  }

  renderHazmatTableFields() {
    const { t } = this.props;
    const { validationErrors } = this.state;
    const hasHazmatError = validationErrors.hasOwnProperty('hazmat_details');
    const isEmptyHazmat = (hazmat) =>
      hazmat.hazmat_class === 'N/A' &&
      hazmat.hazmat_packaging_group === 'N/A' &&
      (hazmat.hazmat_description || '')?.trim() === '';
    const hasEmptyHazmats = this.state.hazmat_details.some(isEmptyHazmat);
    if (featureEnabled(CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS)) {
      return (
        <div className="bg-grey-lightest p-3">
          {this.state.hazmat_details?.map((hazmat, rowIndex) => {
            const showLabels = rowIndex === 0;
            return (
              <div
                className="sm:grid sm:grid-cols-4 sm:gap-4 mb-2"
                key={rowIndex}
              >
                <div className="mb-4">
                  <fieldset className={classnames({ error: hasHazmatError })}>
                    {showLabels && (
                      <label className="block mb-2 mr-2 h-10 form-label">
                        <span className="mr-2 text-grey-darker">
                          {t('HAZMAT_CLASS')}
                        </span>
                      </label>
                    )}
                    <select
                      className="w-full form-select"
                      value={hazmat.hazmat_class}
                      onChange={this.handleHazmatFieldChange(
                        'hazmat_class',
                        rowIndex,
                      )}
                    >
                      {hazmatClasses.map((num) => (
                        <option value={num} key={num}>
                          {num === 'N/A' ? t('N_A') : num}
                        </option>
                      ))}
                    </select>
                  </fieldset>
                </div>
                <div id="" className="mb-4">
                  <fieldset
                    className={classnames({
                      'md:mr-2': true,
                      error: hasHazmatError,
                    })}
                  >
                    {showLabels && (
                      <label className="block mb-2 mr-2 h-10 form-label">
                        <span className="mr-2 text-grey-darker">
                          {t('HAZMAT_PACKAGING_GROUP')}
                        </span>
                      </label>
                    )}
                    <select
                      className="w-full form-select"
                      value={hazmat.hazmat_packaging_group}
                      onChange={this.handleHazmatFieldChange(
                        'hazmat_packaging_group',
                        rowIndex,
                      )}
                    >
                      {hazmatPackagingGroup.map((val) => (
                        <option value={val} key={val}>
                          {val === 'N/A' ? t('N_A') : val}
                        </option>
                      ))}
                    </select>
                  </fieldset>
                </div>
                <div id="" className="">
                  <fieldset className={classnames({ error: hasHazmatError })}>
                    {showLabels && (
                      <label className="block mb-2 mr-2 h-10 form-label">
                        <span className="mr-2 text-grey-darker">
                          {t('HAZMAT_DESCRIPTION')}
                        </span>
                      </label>
                    )}
                    <textarea
                      rows={5}
                      className="block w-full mb-4 form-field"
                      placeholder={t('HAZMAT_DESCRIPTION')}
                      onChange={this.handleHazmatFieldChange(
                        'hazmat_description',
                        rowIndex,
                      )}
                      value={hazmat.hazmat_description}
                    />
                  </fieldset>
                </div>
                <div id="" className="">
                  <fieldset className={classnames()}>
                    {!showLabels && (
                      <button
                        className="button"
                        onClick={() => this.handleRemoveHazmat(rowIndex)}
                        data-testid="remove-hazmat-button"
                      >
                        <Glyphicon glyph="remove" className="text-grey-dark" />
                      </button>
                    )}
                  </fieldset>
                </div>
              </div>
            );
          })}
          <button
            className="mb-4 button button-green"
            onClick={this.handleAddHazmat}
            disabled={hasEmptyHazmats}
          >
            <div className="flex items-center space-x-1">
              <PlusIcon />
              <span>{t('ADD_HAZMAT_CLASS')}</span>
            </div>
          </button>
        </div>
      );
    } else {
      return (
        <div className="sm:grid sm:grid-cols-3 sm:gap-4">
          <div className="mb-4">
            <fieldset className="">
              <label className="block mb-2 mr-2 form-label">
                <span className="mr-2">{t('HAZMAT_CLASS')}</span>
              </label>
              <select
                className="w-full form-select"
                value={this.state.hazmat_class}
                onChange={this.handleFieldChange('hazmat_class')}
              >
                {hazmatClasses.map((num) => (
                  <option value={num} key={num}>
                    {num === 'N/A' ? t('N_A') : num}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
          <div id="" className="mb-4">
            <fieldset className="md:mr-2">
              <label className="block mb-2 mr-2 form-label">
                <span className="mr-2">{t('HAZMAT_PACKAGING_GROUP')}</span>
              </label>
              <select
                className="w-full form-select"
                value={this.state.hazmat_packaging_group}
                onChange={this.handleFieldChange('hazmat_packaging_group')}
              >
                {hazmatPackagingGroup.map((val) => (
                  <option value={val} key={val}>
                    {val === 'N/A' ? t('N_A') : val}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
          <div id="" className="">
            <fieldset className="">
              <label className="block mb-2 mr-2 form-label">
                <span className="mr-2">{t('HAZMAT_DESCRIPTION')}</span>
              </label>
              <textarea
                rows={5}
                className="block w-full mb-4 form-field"
                placeholder={t('HAZMAT_DESCRIPTION')}
                onChange={this.handleFieldChange('hazmat_description')}
                value={this.state.hazmat_description}
              />
            </fieldset>
          </div>
        </div>
      );
    }
  }

  renderHazmatFields() {
    const { t } = this.props;
    return (
      <section className="mb-4">
        <h4 className="mb-4 text-grey-dark">{t('HAZARDOUS_MATERIALS')}</h4>
        <div className="flex flex-wrap">
          <div id="hazardous-toggle" className="md:w-1/3">
            <fieldset className="md:mr-2">
              <label className="inline-block mb-2 mr-2 form-label">
                <span className="mr-2">{t('HAZARDOUS')}</span>
                <input
                  type="checkbox"
                  checked={this.state.hazardous}
                  onChange={(e) => this.handleHazmatCheckboxChange(e)}
                />
              </label>
            </fieldset>
          </div>
        </div>
        {this.state.hazardous && this.renderHazmatTableFields()}
      </section>
    );
  }

  renderOriginDetails() {
    let errors = {};
    const { validationErrors } = this.state;
    const { t } = this.props;
    if (validationErrors.arriving) {
      errors = { address: validationErrors.arriving };
    }
    return (
      <LocationDetails
        id="order-origin-details"
        locationNameId="order-origin-name"
        locationAddressId="order-origin-address"
        details={this.state.arriving}
        sectionTitle={t('ORIGIN_DETAILS')}
        errors={errors}
        onAddContact={this.handleOriginDetailsAddContact}
        onRemoveContact={this.handleOriginDetailsRemoveContact}
        onChange={this.handleOriginDetailsFieldChange}
        onContactFieldChange={this.handleOriginDetailsContactFieldChange}
      >
        {this.renderOriginDateFields()}
      </LocationDetails>
    );
  }

  renderDestinationDetails() {
    let errors = {};
    const { validationErrors } = this.state;
    const { t } = this.props;
    if (validationErrors.delivering) {
      errors = { address: validationErrors.delivering };
    }
    return (
      <LocationDetails
        id="order-destination-details"
        locationNameId="order-destination-name"
        locationAddressId="order-destination-address"
        details={this.state.delivering}
        sectionTitle={t('DESTINATION_DETAILS')}
        errors={errors}
        onAddContact={this.handleDestinationDetailsAddContact}
        onRemoveContact={this.handleDestinationDetailsRemoveContact}
        onChange={this.handleDestinationDetailsFieldChange}
        onContactFieldChange={this.handleDestinationDetailsContactFieldChange}
      >
        {this.renderDestinationDateFields()}
      </LocationDetails>
    );
  }

  renderEquipment() {
    const { t } = this.props;
    return (
      <section className="mb-4">
        <div className="lg:grid lg:grid-cols-2 lg:gap-4">
          <div
            className={this.state.equipments.length !== 0 ? 'col-span-2' : ''}
          >
            <h4 className="mb-4 text-grey-dark">{t('EQUIPMENT')}</h4>
            <fieldset className="mb-4">
              {this.state.equipments.length === 0 && (
                <div>
                  <label className="mb-1 form-label">
                    {t('ADD_EQUIPMENT_NOTE')}
                  </label>
                  <MultiEquipmentInput
                    onChange={(equipmentsToAdd) =>
                      this.setState({ equipmentsToAdd })
                    }
                    value={this.state.equipmentsToAdd}
                    rows={4}
                  />
                  <div className="flex justify-end pt-2">
                    <button
                      className="button button-green"
                      onClick={() => this.handleEquipmentBulkAdd()}
                      data-testid="add-equipment-button"
                    >
                      {titleize(t('ADD'))}
                    </button>
                  </div>
                </div>
              )}
              <div className="mt-2">
                <OrderEquipments
                  equipments={this.state.equipments}
                  onChange={(equipment, key, value) =>
                    this.handleEquipmentChange(equipment, key, value)
                  }
                  onRemoveEquipment={(equipment) =>
                    this.handleEquipmentRemove(equipment)
                  }
                  onAdd={() => this.handleEquipmentAdd()}
                  onApplyAll={(equipment) =>
                    this.handleEquipmentBulkChange(equipment)
                  }
                />
              </div>
            </fieldset>
          </div>
          <div className="mt-4">
            <Documents
              uploadPending={this.state.uploadPending}
              documents={this.state.documentsPendingUpload}
              onDrop={this.handleDrop}
              onCustomValueChange={this.handleDocumentCustomValueChange}
              onTypeChange={this.handleDocumentTypeChange}
              onRemove={this.handleRemoveDocument}
            />
          </div>
        </div>
      </section>
    );
  }

  renderMisc() {
    const { t } = this.props;
    return (
      <section className="mb-4">
        <div className="flex flex-wrap">
          <div className="w-full md:w-1/2">
            <fieldset className="md:mr-4">
              <label className="block mb-1 form-label">
                {t('SPECIAL_INSTRUCTIONS')}
              </label>
              <textarea
                id="order-special-instructions"
                rows={5}
                maxLength={500}
                className="block w-full mb-4 form-field"
                placeholder={t('SPECIAL_INSTRUCTIONS')}
                onChange={this.handleFieldChange('special_instructions')}
                value={this.state.special_instructions}
              />
            </fieldset>
          </div>
          <div className="w-full md:w-1/2">
            <fieldset className="">
              <label className="block mb-1 form-label">
                {t('CARGO_DESCRIPTION')}
              </label>
              <textarea
                id="order-cargo-description"
                rows={5}
                className="block w-full mb-4 form-field"
                placeholder={t('CARGO_DESCRIPTION')}
                onChange={this.handleFieldChange('cargo_description')}
                value={this.state.cargo_description}
              />
            </fieldset>
          </div>
        </div>
      </section>
    );
  }

  renderReferenceNumberError() {
    const { t } = this.props;
    return (
      <div className="w-full">
        <ErrorAlert title={t('DUPLICATE_MASTER_REFERENCE_NUMBER')}>
          <p>{t('MASTER_REFERENCE_NUMBER_ERROR')}</p>
        </ErrorAlert>
      </div>
    );
  }

  renderValidationErrors() {
    const { t } = this.props;
    const { validationErrors } = this.state;
    const { equipment_mismatch } = validationErrors;
    const validationErrorKeys = Object.keys(validationErrors);
    if (validationErrorKeys.length === 0) {
      return null;
    }
    return (
      <div className="w-full">
        <ErrorAlert
          title={t('MISSING_FIELDS')}
          onClose={this.handleClearErrors}
        >
          <ul>
            {validationErrorKeys.map((key) => {
              if (key !== 'equipment_mismatch')
                return <li key={key}>{t(validationErrors[key])}</li>;
            })}
            {Object.keys(equipment_mismatch || {})?.length > 0 &&
              Object.keys(equipment_mismatch).map((equipment) => (
                <li key={equipment}>
                  {equipment}: {t('MISMATCHED_EQUIPMENT_TYPE')}
                </li>
              ))}
          </ul>
        </ErrorAlert>
      </div>
    );
  }

  renderOrderTemplateActions() {
    if (!SessionStore.hasOrderTemplateAdminRole()) {
      return null;
    }
    const { t } = this.props;
    const buttons = [];

    if (this.state.templateId && !this.state.saveAsToggled) {
      buttons.push(
        <div className="mb-4 sm:mb-0">
          <button
            id="order-saveas-button"
            className="block button button-hollow-green"
            onClick={this.handleToggleSaveAs}
          >
            {t('SAVE_AS')}
          </button>
        </div>,
      );
    }

    if (!this.state.templateId || this.state.saveAsToggled) {
      buttons.push(
        <div className="w-full sm:w-64 mb-4 sm:mb-0">
          <CargomaticInput
            type="text"
            hideLabel
            name="template-name"
            className="w-full"
            placeholder={t('TEMPLATE_NAME')}
            value={this.state.name}
            onChange={this.handleTemplateNameChange}
          />
        </div>,
      );
    }

    buttons.push(
      <div className="mb-4 sm:mb-0">
        <button
          id="order-create-order-template-button"
          className="block button button-hollow-green w-full sm:w-auto"
          disabled={
            this.state.orderPending ||
            this.state.accountingHold ||
            !this.state.name
          }
          onClick={this.handleCreateOrderAndTemplate}
        >
          {this.state.templateId && <span>{t('SAVE_AND_BOOK_ORDER')}</span>}

          {!this.state.templateId && <span>{t('CREATE_AND_BOOK_ORDER')}</span>}
        </button>
      </div>,
    );
    return (
      <div className="sm:grid sm:grid-cols-2 sm:gap-4 mb-4">{buttons}</div>
    );
  }

  renderActions() {
    const { t } = this.props;
    const isLegManagementEnabled = this.props.flags?.legManagement === true;
    return (
      <div className="actions-wrapper">
        <div className="sm:flex sm:justify-end sm:-mx-2">
          <div>
            {this.renderOrderTemplateActions()}
            <hr className="sm:hidden my-4 sm:my-0" />
            <div className="sm:flex sm:flex-wrap sm:justify-end sm:flex-auto sm:px-2">
              <div className="sm:flex sm:items-center sm:px-2 button-group">
                {isLegManagementEnabled && (
                  <div className="flex items-center space-x-2 mr-2">
                    <Switch
                      id="leg-management-opt-in"
                      checked={this.state.leg_management_version === 2}
                      onCheckedChange={this.handleToggleLegManagementVersion}
                    />
                    <Label htmlFor="leg-management-opt-in">
                      Enable leg management
                    </Label>
                  </div>
                )}
                <Checkbox
                  id="autocreate_shipments"
                  name="autocreate_shipments"
                  checked={this.state.autocreate_shipments}
                  className="mr-2 mb-2 sm:mb-0"
                  onChange={this.handleToggleAutocreateShipments}
                >
                  {t('AUTOCREATE_SHIPMENTS')}
                </Checkbox>
                <Button
                  variant="primary"
                  id="order-create-order-button"
                  className="mr-2 w-full sm:w-auto mb-4 sm:mb-0"
                  disabled={
                    this.state.orderPending || this.state.accountingHold
                  }
                  onClick={this.handleCreateOrder}
                >
                  {this.state.orderPending && (
                    <i className="mr-2 fa fa-circle-o-notch fa-spin" />
                  )}
                  {t('CREATE_ORDER')}
                </Button>
                <Button
                  variant="secondary"
                  className="secondary-action w-full sm:w-auto mb-4 sm:mb-0"
                  onClick={this.handleClose}
                >
                  {t('CANCEL')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderOriginDateFields() {
    const { order_type, last_free_date } = this.state;
    const { t } = this.props;
    if (order_type !== IMPORT) {
      return null;
    }
    return (
      <fieldset className="">
        <label className="block mb-1 form-label">{t('LAST_FREE_DAY')}</label>
        <div className="block mb-4">
          <DatePicker
            className="block w-full form-field"
            selected={last_free_date}
            placeholderText={t('LAST_FREE_DAY')}
            locale={this.props.localeData.languageCodeId}
            onSelect={this.handleDateFieldChange('last_free_date')}
          />
        </div>
      </fieldset>
    );
  }

  renderDestinationDateFields() {
    const { order_type, last_free_date } = this.state;
    const { t } = this.props;
    if (order_type !== EXPORT) {
      return null;
    }
    return (
      <>
        <fieldset className="">
          <label className="block mb-1 form-label">
            {t('EARLIEST_DATE_TO_GET_EMPTY')}
          </label>
          <div className="block mb-4">
            <DatePicker
              className="block w-full form-field"
              placeholderText={t('EARLIEST_DATE_TO_GET_EMPTY')}
              locale={this.props.localeData.languageCodeId}
              selected={this.state.earliest_empty}
              onSelect={this.handleDateFieldChange('earliest_empty')}
            />
          </div>
        </fieldset>
        <fieldset className="">
          <label className="block mb-1 form-label">
            {t('EARLIEST_RETURN_DATE')}
          </label>
          <div className="block mb-4">
            <DatePicker
              className="block w-full form-field"
              placeholderText={t('EARLIEST_RETURN_DATE')}
              locale={this.props.localeData.languageCodeId}
              selected={this.state.earliest_return_date}
              isClearable
              onSelect={this.handleDateFieldChange('earliest_return_date')}
            />
          </div>
        </fieldset>
        <fieldset className="">
          <label className="block mb-1 form-label">{t('LAST_CUT_DAY')}</label>
          <div className="block mb-4">
            <DatePicker
              className="block w-full form-field"
              selected={last_free_date}
              placeholderText={t('LAST_CUT_DAY')}
              locale={this.props.localeData.languageCodeId}
              onSelect={this.handleDateFieldChange('last_free_date')}
            />
          </div>
        </fieldset>
      </>
    );
  }

  render() {
    const { t } = this.props;
    const { shipper, templates, templateId } = this.state;
    const shipperIdPresent = shipper._id;
    const templatesAvailable = templates.length > 0;

    const noTemplateAlert = (
      <Alert type="warning">{t('NO_ORDER_TEMPLATE_FOUND_FOR_SHIPPER')}</Alert>
    );

    const selectShipperAlert = (
      <Alert type="warning">{t('SELECT_SHIPPER_TO_VIEW_TEMPLATE_LIST')}</Alert>
    );

    const orderTemplatesDropdown = (
      <select
        onChange={this.handleTemplateSelect}
        className="form-select"
        value={templateId}
        data-testid="order-templates-dropdown"
      >
        <option value="">{t('POPULATE_TEMPLATE')}</option>
        {templates.map((template) => (
          <option value={template._id} key={template._id}>
            {template.name}
          </option>
        ))}
      </select>
    );

    function getOrderTemplateDropDown(shipperIdPresent, templatesAvailable) {
      if (!shipperIdPresent) {
        return selectShipperAlert;
      }

      if (!templatesAvailable) {
        return noTemplateAlert;
      }

      return orderTemplatesDropdown;
    }

    return (
      <Dialog open={this.props.show} onOpenChange={this.handleClose}>
        <DialogContent className="w-screen h-screen max-w-none max-h-none flex flex-col">
          <DialogHeader>
            <DialogTitle>{t('CREATE_NEW_ORDER')}</DialogTitle>
            {this.state.templateFetchPending ? (
              <LoadingCircle text={t('LOADING')} />
            ) : (
              getOrderTemplateDropDown(shipperIdPresent, templatesAvailable)
            )}
          </DialogHeader>
          <ScrollArea className="flex-1 px-6 pb-6">
            <div ref={this.errorRef}>{this.renderValidationErrors()}</div>
            {this.renderCustomerDetails()}
            {this.renderOrderDetails()}
            {this.renderReferenceNumbers()}
            {this.renderRailwayDetails()}
            {this.renderHazmatFields()}
            {this.renderOriginDetails()}
            {this.renderDestinationDetails()}
            {this.renderEquipment()}
            {this.renderMisc()}
          </ScrollArea>
          <div className="flex-shrink-0 border-t bg-white px-6 py-4">
            {this.renderActions()}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
}

function addReferenceNumber() {
  return {
    name: '',
    value: '',
    hidden: false,
    editable: true,
  };
}

function addContact() {
  return {
    name: '',
    phone: '',
  };
}

const defaultLocation = {
  location: {
    address: '',
    coords: [],
    lat: null,
    lng: null,
    contacts: [],
  },
};

function applyTemplate(template) {
  const {
    hazmat_class,
    hazmat_packaging_group,
    hazmat_description,
    hazmat_details,
    service_type,
    movement_type,
    order_type,
    mode,
    beneficial_cargo_owner,
    estimated_arrival: estimatedArrivalDate,
    arrival_date: arrivalDate,
    earliest_empty: earliestEmptyDate,
    earliest_return_date: earliestReturnDate,
    originating: originatingLocations,
    arriving: arrivingLocations,
    delivering: deliveringLocations,
    last_free_date: lastFreeDate,
    special_instructions,
    name,
  } = template;

  let arrival_date = null;
  if (arrivalDate) {
    arrival_date = moment(arrivalDate);
  }

  let estimated_arrival = null;
  if (estimatedArrivalDate) {
    estimated_arrival = moment(estimatedArrivalDate);
  }

  let earliest_empty = null;
  if (earliestEmptyDate) {
    earliest_empty = moment(earliestEmptyDate);
  }

  let earliest_return_date = null;
  if (earliestReturnDate) {
    earliest_return_date = moment(earliestReturnDate);
  }

  let last_free_date = null;
  if (lastFreeDate) {
    last_free_date = moment(lastFreeDate);
  }

  const hazardous = featureEnabled(
    CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS,
  )
    ? !!(
        hazmat_details?.length &&
        hazmat_details.some(
          (h) =>
            (h.hazmat_class && h.hazmat_class !== 'N/A') ||
            (h.hazmat_packaging_group && h.hazmat_packaging_group !== 'N/A') ||
            h.hazmat_description,
        )
      )
    : (hazmat_class && hazmat_class !== 'N/A') ||
      (hazmat_packaging_group && hazmat_packaging_group !== 'N/A') ||
      hazmat_description;

  const arriving = mapLocation(arrivingLocations);
  const delivering = mapLocation(deliveringLocations);
  const originating = mapLocation(originatingLocations);

  const originating_location = getOriginatingLocationLabel(order_type);

  const templateShipper = template.shipper || {};
  const templateCarrier = template.carrier || {};

  const shipper = {
    name: templateShipper.company_name || '',
    _id: templateShipper._id || null,
  };

  const carrier = {
    name: templateCarrier.name || '',
    _id: templateCarrier._id || null,
    status: templateCarrier.status || null,
  };

  return {
    name,
    arrival_date,
    estimated_arrival,
    earliest_empty,
    earliest_return_date,
    last_free_date,
    arriving,
    delivering,
    originating,
    hazardous,
    hazmat_class,
    hazmat_packaging_group,
    hazmat_description,
    hazmat_details,
    service_type,
    movement_type,
    originating_location,
    order_type,
    mode,
    beneficial_cargo_owner,
    shipper,
    carrier,
    special_instructions,
  };
}

function getInitialState(localeData, propsInitialState = {}) {
  let order_type = IMPORT;
  let mode = DRAYAGE;
  let arriving_location_type = TERMINAL;
  let delivering_location_type = CUSTOMER;

  if (order_type === DOMESTIC) {
    mode = INTERMODAL;
    arriving_location_type = CUSTOMER;
    delivering_location_type = CUSTOMER;
  }

  return {
    leg_management_version: 1,
    name: '',
    templates: [],
    templateId: '',
    saveAsToggled: false,
    cargo_description: '',
    hazardous: false,
    hazmat_class: 'N/A',
    hazmat_packaging_group: 'N/A',
    hazmat_description: '',
    hazmat_details: [],
    validationErrors: {},
    service_type: LIVE,
    movement_type: DELIVERY,
    originating_location: OverseasPort,
    order_type,
    mode,
    order_received_date: moment(),
    beneficial_cargo_owner: '',
    reefer_temperature: '',
    reefer_temperature_unit:
      localeData.temperature_units || DEFAULT_TEMPERATURE_UNIT,
    certified_weight: '',
    count: '',
    orderPending: false,
    shipper: {
      _id: '',
      company_name: '',
    },
    carrierPreferences: {
      preferred: [],
      excluded: [],
    },
    carrier: {
      _id: '',
      name: '',
    },
    master_bol: '',
    delivery_order: '',
    referenceNumbers: [],
    originating: {
      location: {
        address: '',
        coords: [],
        lat: null,
        lng: null,
        contacts: [],
      },
    },
    estimated_arrival: moment(),
    vessel_or_rail: '',
    train: '',
    railroads: [],
    railroadItems: [],
    ocean_carrier_scac: '',
    custom_ocean_carrier_scac: '',
    arrival_date: null,
    earliest_empty: null,
    earliest_return_date: null,
    arriving: {
      name: '',
      location_type: arriving_location_type,
      location: {
        address: '',
        coords: [],
        lat: null,
        lng: null,
      },
      contacts: [],
    },
    delivering: {
      name: '',
      contacts: [],
      location_type: delivering_location_type,
      location: {
        address: '',
        coords: [],
        lat: null,
        lng: null,
      },
    },
    last_free_date: null,
    special_instructions: '',
    total_weight: '',
    weight_units: localeData.weight_units || DEFAULT_WEIGHT_UNIT,
    documentsPendingUpload: [],
    orderDocuments: [],
    uploadPending: false,
    documentErrorFields: [],
    equipments: [],
    equipmentVal: '',
    addEquipmentFormVisible: false,
    duplicateMasterRefExists: false,
    accountingHold: false,
    autocreate_shipments: true,
    requiredReferenceNumbers: {},
    requiredAtBooking: [],
    optional: [],
    templateFetchPending: false,
    ...propsInitialState,
  };
}

function mapLocation(locations) {
  if (!locations || locations.length === 0) {
    return {
      ...defaultLocation,
    };
  }
  const { name, contact, location_type, address, coords, lat, lng, place_id } =
    locations[0];
  const contacts = contact ? [contact] : [];

  const location = {
    name,
    contacts,
    location_type,
    location: {
      address,
      coords,
      lat,
      lng,
      place_id,
    },
  };

  return location;
}

function getOriginatingLocationLabel(order_type) {
  let originating_location = OverseasPort;
  if (order_type === DOMESTIC) {
    originating_location = UltimateDestination;
  } else if (order_type === EXPORT) {
    originating_location = FinalDestination;
  } else if (order_type === IMPORT) {
    originating_location = OverseasPort;
  }
  return originating_location;
}

function getRandomKey() {
  return `order-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`;
}

export default withTranslation()(
  withLocaleContext(withFlags(CreateOrderModal)),
);
